import React, { useState } from 'react';
import { Button, DatePicker, Popover, Segmented } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import moment from 'moment';
import type { RangeValue } from 'rc-picker/lib/interface';
import type { Moment } from 'moment';
import {
    handleDatePickerChange,
    isOutsideMaxPeriod,
} from '~utils/date-picker-helpers';
import { PERIOD_PRESETS } from '../types';
import { PERIOD_PRESET_LABELS } from '../resolvers';
import { lastSelectableDay } from '../periods';
import { ProvinceCodesSelectTree } from '~components/region-selectors/province-codes-select-tree';
import { clampPickedRange, describePeriod } from './card-edit-menu.utils';
import type { Period, PeriodPreset, Region } from '../types';
import style from './card-edit-menu.module.scss';

const { RangePicker } = DatePicker;

const DATE_FORMAT = 'YYYY-MM-DD';

/** Sentinel value used in the Segmented control to represent "custom range". */
const CUSTOM_SENTINEL = '__custom__';

/** Label for the custom-range option in the mode control. */
const CUSTOM_LABEL = 'Custom range';

/** Options shown in the mode segmented control. */
const PRESET_OPTIONS = [
    ...PERIOD_PRESETS.map((preset) => ({
        label: PERIOD_PRESET_LABELS[preset],
        value: preset,
    })),
    { label: CUSTOM_LABEL, value: CUSTOM_SENTINEL },
];

export interface CardEditMenuProps {
    effectivePeriod: Period;
    onChange: (period: Period) => void;
    effectiveRegion: Region;
    onRegionChange: (region: Region) => void;
    onReset: () => void;
}

/**
 * Per-card date-range and region edit control. Renders a pen-icon button that opens an antd
 * Popover containing:
 *   - a Segmented control for named presets (+ "Custom range")
 *   - a RangePicker (shown only in custom mode)
 *   - a "Reset to default" button
 *
 * This component holds NO fetch logic. It receives the effective period and
 * emits changes; the bridge (KpiCardFromSpec) owns the override state.
 */
export const CardEditMenu: React.FC<CardEditMenuProps> = ({
    effectivePeriod,
    onChange,
    effectiveRegion,
    onRegionChange,
    onReset,
}) => {
    const [open, setOpen] = useState(false);
    // Same disabledDate mechanism as the page picker; see date-picker-helpers.
    const [picking, setPicking] = useState<RangeValue<Moment>>(null);

    const { mode, bounds } = describePeriod(effectivePeriod);

    /** The Segmented value: the preset token or the custom sentinel. */
    const segmentedValue =
        mode === 'preset' ? (effectivePeriod as PeriodPreset) : CUSTOM_SENTINEL;

    // The popover stays open while the user adjusts the period and/or region, so
    // both can be set in one session; it closes on outside-click or "Reset".
    const handleSegmentChange = (value: string | number) => {
        if (value === CUSTOM_SENTINEL) {
            // Switch to custom mode on the last day a range may end on.
            const last = lastSelectableDay();
            onChange({ start: last, end: last });
        } else {
            onChange(value as PeriodPreset);
        }
    };

    const handleRangeChange = (dates: RangeValue<Moment>) => {
        handleDatePickerChange(dates, (validDates) => {
            onChange(clampPickedRange(validDates));
        });
    };

    const handleReset = () => {
        onReset();
        setOpen(false);
    };

    const content = (
        <div className={style.menu_content}>
            <Segmented
                options={PRESET_OPTIONS}
                value={segmentedValue}
                onChange={handleSegmentChange}
                className={style.segmented}
            />
            {mode === 'custom' && (
                <RangePicker
                    className={style.range_picker}
                    value={
                        bounds
                            ? [moment(bounds.start), moment(bounds.end)]
                            : null
                    }
                    onChange={handleRangeChange}
                    disabledDate={(current) =>
                        isOutsideMaxPeriod(current, picking) ||
                        current.format(DATE_FORMAT) > lastSelectableDay()
                    }
                    onCalendarChange={setPicking}
                    onOpenChange={(isOpen) =>
                        setPicking(isOpen ? [null, null] : null)
                    }
                    format={DATE_FORMAT}
                />
            )}
            <ProvinceCodesSelectTree
                value={effectiveRegion}
                onChange={onRegionChange}
            />
            <Button
                size="small"
                type="text"
                className={style.reset_button}
                onClick={handleReset}
            >
                Reset to default
            </Button>
        </div>
    );

    return (
        <Popover
            content={content}
            trigger="click"
            open={open}
            onOpenChange={setOpen}
            placement="bottomRight"
        >
            <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                className={style.trigger_button}
                aria-label="Edit card settings"
            />
        </Popover>
    );
};
